import { Form } from 'formik';
import { FormikDebounce } from '../../common/form/FormikDebounce';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../../store';
import { getUser, selectUser, selectUserState, userActions } from '../../../store/entities/user';
import { qcsAdminUserListActions } from '../../../store/entities/qcsAdminUserList';
import { QcsMenuItem } from '../../common/basic/QcsMenuItem';
import { DetailHeader } from '../../common/DetailHeader';
import { FormContainer } from '../../common/form/FormContainer';
import { Input } from '../../common/form/Input';
import { LanguageSelect } from '../../common/form/LanguageSelect';
import { Select } from '../../common/form/Select';
import { SubmitButton } from '../../common/form/SubmitButton';
import * as Yup from 'yup';
import { validations } from '../../../utils/validations';
import { SUPPORTED_LANGUAGES } from '../../../utils/i18n';
import { UserRoleSelect } from '../../common/form/UserRoleSelect';
import { PhoneInput } from '../../common/form/PhoneInput';
import { useNavigation } from '../../../utils/urlPaths';
import { UserRole } from '@api/models/enums/UserRole';
import { UserState } from '@api/models/enums/UserState';
import { ShowResourceContent } from '../../ShowResourceContent';
import { resources } from '@api/resources';
import { UserRequest } from '@api/models/user/UserRequest';
import { userService } from '@api/services/services';
import { ShowError } from '../../ShowError';

export const QcsAdminUserDetail: FC = () => {
  const { t } = useTranslation();
  const { goBack, getBackPath: backTo } = useNavigation('/qcsuser');
  const { userId } = useParams();
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (userId === 'new') {
      dispatch(userActions.default());
    } else {
      dispatch(getUser(userId!));
    }
  }, [userId, dispatch]);

  const handleSubmit = async (data: UserRequest) => {
    if (userId === 'new') {
      await userService.create(data);
    } else {
      await userService.update(userId!, data);
    }

    dispatch(qcsAdminUserListActions.reload(true));
    goBack();
  };

  const validate = (values: UserRequest) => {
    const errors: Partial<Record<keyof UserRequest, string>> = {};

    if (
      values.roles.some((role) => role === UserRole.Tourniquet || role === UserRole.TourniquetTraining || role === UserRole.Training) &&
      values.roles.length !== 1
    ) {
      errors.roles = t('user.errorInvalidRoleCombination') ?? undefined;
    }

    return errors;
  };

  return (
    <>
      <DetailHeader title="admin.user.detailTitle" backTo={backTo} />
      <ShowResourceContent resource={resources.getUser} fetchStateSelector={selectUserState}>
        <FormikDebounce<UserRequest>
          initialValues={{
            id: user.id,
            name: user.name,
            lastName: user.lastName,
            email: user.email ?? '',
            state: user.state,
            phone: user.phone,
            establishments: user.establishments,
            note: user.note,
            office: user.office,
            language: user.language ?? '',
            roles: userId === 'new' ? [UserRole.AdminQcs] : user.roles,
          }}
          validationSchema={Yup.object().shape({
            name: validations.stringRequired(t),
            lastName: validations.stringRequired(t),
            email: validations.emailRequired(t),
            phone: validations.phoneOptional(t),
            roles: validations.arrayNotEmpty(t),
          })}
          validate={validate}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          <Form>
            <FormContainer>
              <Input name="name" label={t('admin.user.name')} maxLength={100} required />
              <Input name="lastName" label={t('admin.user.lastName')} maxLength={100} required />
              <PhoneInput name="phone" label={t('admin.company.phone')} />
              <Input name="email" removeSpaces label={t('admin.user.email')} maxLength={100} required />
              <LanguageSelect name="language" label={t('admin.user.language')} languages={SUPPORTED_LANGUAGES} />

              <UserRoleSelect name="roles" label={t('admin.user.roles')} disabled={true} required />

              <Select name="state" label={t('admin.user.state')}>
                <QcsMenuItem value={UserState.Active} disabled={user.state !== UserState.Active}>
                  {t('entityState.ACTIVE')}
                </QcsMenuItem>
                <QcsMenuItem value={UserState.Inactive}>{t('entityState.INACTIVE')}</QcsMenuItem>
                <QcsMenuItem value={UserState.Invited}>{t('entityState.INVITED')}</QcsMenuItem>
              </Select>

              <ShowError resource={[resources.createQcsUser, resources.updateUser]} />
              <SubmitButton />
            </FormContainer>
          </Form>
        </FormikDebounce>
      </ShowResourceContent>
    </>
  );
};
